import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  Clock,
  ExternalLink,
  FileText,
  Loader2,
  ShieldCheck,
  UserCheck,
  XCircle,
} from 'lucide-react';
import { deciderControleVisuel, fetchControleVisuel } from '../services/api';

import { dateFr } from '../utils/dates';
const dateComplete = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * Contrôle d'un visuel déposé par un sponsor.
 *
 * La page s'ouvre depuis les liens du courriel du bureau. Le jeton porté par
 * l'adresse tient lieu d'autorisation : on relève ses courriels sur un
 * téléphone, pas toujours connecté au site.
 *
 * Quand l'adresse porte déjà une décision, celle-ci est appliquée à
 * l'ouverture — c'est le clic du bureau qui l'a demandée. L'appel part en POST
 * depuis la page, et non par la simple visite du lien : les antivirus de
 * messagerie ouvrent les adresses avant leur destinataire, et trancheraient à
 * sa place.
 *
 * Si le visuel a déjà été jugé — depuis le bot, ou par un autre membre — rien
 * n'est réappliqué : la page montre le visuel, le sponsor, et qui a tranché.
 */
export default function ControleVisuel() {
  const { jeton, decision } = useParams<{ jeton: string; decision?: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const demandeFaite = useRef(false);

  const [erreur, setErreur] = useState<string | null>(null);
  const [motif, setMotif] = useState('');
  const [saisieMotif, setSaisieMotif] = useState(false);

  const { data, isPending, isError } = useQuery({
    queryKey: ['controle-visuel', jeton],
    queryFn: () => fetchControleVisuel(jeton as string),
    enabled: Boolean(jeton),
    retry: false,
  });

  const visuel = data?.visuel;

  const decider = useMutation({
    mutationFn: (choix: { decision: 'valide' | 'refuse'; motif?: string }) =>
      deciderControleVisuel(jeton as string, choix),
    onSuccess: () => {
      setErreur(null);
      setSaisieMotif(false);
      queryClient.invalidateQueries({ queryKey: ['controle-visuel', jeton] });
    },
    onError: (e: Error) => setErreur(e.message),
  });

  // La décision portée par l'adresse s'applique une fois, à l'ouverture.
  useEffect(() => {
    if (demandeFaite.current || !visuel?.enAttente) return;
    if (decision !== 'valide' && decision !== 'refuse') return;

    demandeFaite.current = true;

    // Un refus mérite un motif : le sponsor doit pouvoir corriger sans
    // téléphoner. La page le demande au lieu d'appliquer aussitôt.
    if (decision === 'refuse') {
      setSaisieMotif(true);
      // L'adresse est nettoyée : un rechargement ne doit pas relancer le geste.
      navigate(`/visuel/${jeton}`, { replace: true });
      return;
    }

    decider.mutate({ decision: 'valide' });
    navigate(`/visuel/${jeton}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visuel?.enAttente, decision]);

  if (isPending) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (isError || !visuel) {
    return (
      <div className="p-4 lg:p-0 pb-24 lg:pb-0">
        <div className="card bg-red-50 border-red-200 text-red-800 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 mt-0.5" />
          <div>
            <p className="font-semibold">Lien invalide</p>
            <p className="text-sm mt-1">
              Ce lien ne correspond à aucun visuel. Il a peut-être été remplacé par un dépôt plus
              récent.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const estImage =
    typeof visuel.mimeType === 'string' && visuel.mimeType.startsWith('image/');

  return (
    <div className="p-4 lg:p-0 space-y-5 pb-24 lg:pb-0 max-w-2xl">
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <ShieldCheck className="w-7 h-7 text-primary-700" />
          Contrôle du visuel
        </h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{visuel.titre}</p>
      </div>

      {erreur && <p className="text-sm rounded-xl px-3 py-2 bg-red-50 text-red-700">{erreur}</p>}

      {/* Le visuel lui-même : on ne juge pas sur un nom de fichier. */}
      <section className="card p-0 overflow-hidden">
        <div className="bg-gray-50 dark:bg-gray-800 flex items-center justify-center min-h-[12rem] p-4">
          {estImage ? (
            <img
              src={visuel.url}
              alt={visuel.nomOriginal ?? visuel.titre}
              className="max-h-80 w-auto object-contain"
            />
          ) : (
            <div className="text-center space-y-2">
              <FileText className="w-12 h-12 mx-auto text-gray-400" />
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Ce format ne s'affiche pas ici.
              </p>
            </div>
          )}
        </div>
        <div className="p-4 space-y-1">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 break-all">
            {visuel.nomOriginal}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {visuel.tailleFormatee}
            {visuel.deposeLe && (
              <> · déposé le {dateFr(visuel.deposeLe)}</>
            )}
          </p>
          <a
            href={visuel.url}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-primary-700 dark:text-primary-400 hover:underline inline-flex items-center gap-1"
          >
            Ouvrir le fichier
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </section>

      {/* Le sponsor : on contrôle pour quelqu'un, pas dans l'abstrait. */}
      {visuel.sponsor && (
        <section className="card lg:p-5 space-y-1">
          <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Déposé par
          </p>
          <p className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-gray-400" />
            {visuel.sponsor.nom}
          </p>
          {visuel.sponsor.adresse && (
            <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-line">
              {visuel.sponsor.adresse}
            </p>
          )}
          {visuel.sponsor.siteWeb && (
            <a
              href={visuel.sponsor.siteWeb}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-primary-700 dark:text-primary-400 hover:underline inline-flex items-center gap-1 break-all"
            >
              {visuel.sponsor.siteWeb}
              <ExternalLink className="w-3.5 h-3.5 shrink-0" />
            </a>
          )}
        </section>
      )}

      {/* Décision */}
      {visuel.enAttente ? (
        saisieMotif ? (
          <section className="card lg:p-5 space-y-3">
            <p className="font-medium text-gray-900 dark:text-gray-100">Motif du refus</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Il est transmis tel quel au sponsor : il doit lui permettre de corriger sans avoir
              à téléphoner.
            </p>
            <textarea
              className="input min-h-[5rem]"
              placeholder="Résolution trop faible pour une impression en A4…"
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
            />
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => decider.mutate({ decision: 'refuse', motif: motif.trim() })}
                disabled={decider.isPending}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold disabled:opacity-50"
              >
                {decider.isPending ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <XCircle className="w-5 h-5" />
                )}
                Confirmer le refus
              </button>
              <button
                onClick={() => setSaisieMotif(false)}
                className="text-sm text-gray-500 dark:text-gray-400"
              >
                Annuler
              </button>
            </div>
          </section>
        ) : (
          <section className="card lg:p-5 space-y-3">
            <p className="font-medium text-gray-900 dark:text-gray-100">
              Ce visuel est-il conforme ?
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => decider.mutate({ decision: 'valide' })}
                disabled={decider.isPending}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-green-600 hover:bg-green-700 text-white font-semibold disabled:opacity-50"
              >
                {decider.isPending ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-5 h-5" />
                )}
                Approuver
              </button>
              <button
                onClick={() => setSaisieMotif(true)}
                disabled={decider.isPending}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold disabled:opacity-50"
              >
                <XCircle className="w-5 h-5" />
                Refuser
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Le sponsor est informé de la décision. Le bot Telegram cesse alors de proposer ses
              boutons pour ce visuel.
            </p>
          </section>
        )
      ) : (
        <section
          className={`rounded-2xl border-2 p-5 space-y-3 ${
            visuel.statut === 'valide'
              ? 'border-green-300 bg-green-50 dark:bg-green-950/30'
              : 'border-red-300 bg-red-50 dark:bg-red-950/30'
          }`}
        >
          <div className="flex items-start gap-3">
            {visuel.statut === 'valide' ? (
              <CheckCircle2 className="w-7 h-7 text-green-600 dark:text-green-400 shrink-0" />
            ) : (
              <XCircle className="w-7 h-7 text-red-600 dark:text-red-400 shrink-0" />
            )}
            <div>
              <p
                className={`text-lg font-bold ${
                  visuel.statut === 'valide'
                    ? 'text-green-900 dark:text-green-100'
                    : 'text-red-900 dark:text-red-100'
                }`}
              >
                {visuel.statut === 'valide' ? 'Visuel validé' : 'Visuel refusé'}
              </p>
              {visuel.motifRefus && (
                <p className="text-sm text-red-800 dark:text-red-200 mt-1">{visuel.motifRefus}</p>
              )}
            </div>
          </div>

          <dl className="text-sm bg-white/70 dark:bg-gray-900/40 rounded-xl p-3 space-y-1.5">
            {visuel.controlePar && (
              <div className="flex items-start gap-2">
                <UserCheck className="w-4 h-4 shrink-0 mt-0.5 text-gray-500" />
                <div>
                  <dt className="text-xs text-gray-500 dark:text-gray-400">Par</dt>
                  <dd className="font-semibold text-gray-900 dark:text-gray-100">
                    {visuel.controlePar}
                  </dd>
                </div>
              </div>
            )}
            {visuel.controleLe && (
              <div className="flex items-start gap-2">
                <Clock className="w-4 h-4 shrink-0 mt-0.5 text-gray-500" />
                <div>
                  <dt className="text-xs text-gray-500 dark:text-gray-400">Le</dt>
                  <dd className="font-semibold text-gray-900 dark:text-gray-100 first-letter:uppercase">
                    {dateComplete.format(new Date(visuel.controleLe))}
                  </dd>
                </div>
              </div>
            )}
          </dl>
        </section>
      )}
    </div>
  );
}
